var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Félix Robles <felix@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext, useEffect, Suspense, lazy } from "react";
import { useRecordContext, useSidebarState } from "react-admin";
import ElectionHeader from "@/components/ElectionHeader";
import { AuthContext } from "@/providers/AuthContextProvider";
import { IPermissions } from "@/types/keycloak";
import { useTranslation } from "react-i18next";
import { useElectionEventTallyStore } from "@/providers/ElectionEventTallyProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { EPublishType } from "../Publish/EPublishType";
import { EElectionEventLockedDown } from "@sequentech/ui-core";
import { Box, CircularProgress } from "@mui/material";
import { Tabs } from "@/components/Tabs";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
// Lazy load the tab components
var DashboardElectionEvent = lazy(function () { return import("@/components/dashboard/election-event/Dashboard"); });
var OVOFDashboardElectionEvent = lazy(function () { return import("@/components/monitoring-dashboard/election-event/MonitoringDashboard"); });
var EditElectionEventData = lazy(function () {
    return import("./EditElectionEventData").then(function (module) { return ({ default: module.EditElectionEventData }); });
});
var EditElectionEventTextData = lazy(function () {
    return import("./EditElectionEventTextData").then(function (module) { return ({ default: module.default }); });
});
var EditElectionEventUsers = lazy(function () {
    return import("./EditElectionEventUsers").then(function (module) { return ({ default: module.EditElectionEventUsers }); });
});
var EditElectionEventAreas = lazy(function () {
    return import("./EditElectionEventAreas").then(function (module) { return ({ default: module.EditElectionEventAreas }); });
});
var EditElectionEventKeys = lazy(function () {
    return import("./EditElectionEventKeys").then(function (module) { return ({ default: module.EditElectionEventKeys }); });
});
var EditElectionEventTally = lazy(function () {
    return import("./EditElectionEventTally").then(function (module) { return ({ default: module.EditElectionEventTally }); });
});
var Publish = lazy(function () {
    return import("@/resources/Publish/Publish").then(function (module) { return ({ default: module.Publish }); });
});
var ElectoralLog = lazy(function () {
    return import("./ElectoralLog").then(function (module) { return ({ default: module.ElectoralLog }); });
});
var EditElectionEventTasks = lazy(function () {
    return import("./EditElectionEventTasks").then(function (module) { return ({ default: module.EditElectionEventTasks }); });
});
var EditElectionEventEvents = lazy(function () {
    return import("./EditElectionEventScheduledEvents").then(function (module) { return ({
        default: module.EditElectionEventEvents,
    }); });
});
var EditElectionEventApprovals = lazy(function () {
    return import("./EditElectionEventApprovals").then(function (module) { return ({
        default: module.EditElectionEventApprovals,
    }); });
});
var EditElectionEventReports = lazy(function () {
    return import("../Reports/EditReportsTab").then(function (module) { return ({
        default: module.EditReportsTab,
    }); });
});
export var ElectionEventTabs = function () {
    var _a;
    var record = useRecordContext();
    var authContext = useContext(AuthContext);
    var _b = React.useState(null), showKeysList = _b[0], setShowKeysList = _b[1];
    var _c = React.useState(), showTaskList = _c[0], setShowTaskList = _c[1];
    var _d = React.useState(), showPublishList = _d[0], setShowPublishList = _d[1];
    var _e = React.useState(), showApprovalList = _e[0], setShowApprovalList = _e[1];
    var location = useLocation();
    var navigate = useNavigate();
    var refreshRef = React.useRef();
    var t = useTranslation().t;
    var isElectionEventLocked = ((_a = record === null || record === void 0 ? void 0 : record.presentation) === null || _a === void 0 ? void 0 : _a.locked_down) == EElectionEventLockedDown.LOCKED_DOWN;
    var setTallyId = useElectionEventTallyStore().setTallyId;
    var open = useSidebarState()[0];
    var aliasRenderer = useAliasRenderer();
    var showDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ADMIN_DASHBOARD_VIEW);
    var showMonitoringDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITORING_DASHBOARD_VIEW_ELECTION_EVENT);
    var showData = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_DATA_TAB);
    var showTextData = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_DATA_TAB);
    var showVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_VOTERS_TAB);
    var showAreas = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_AREAS_TAB);
    var showKeys = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, [
            IPermissions.ADMIN_CEREMONY,
            IPermissions.TRUSTEE_CEREMONY,
        ]) &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_KEYS_TAB);
    var showTally = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, [
            IPermissions.TALLY_READ,
            IPermissions.TALLY_START,
        ]) &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_TALLY_TAB);
    var showPublish = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_PUBLISH_TAB);
    var showLogs = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_LOGS_TAB);
    var showTasksExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_TASKS_TAB);
    var showEvents = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_SCHEDULED_TAB);
    var showNotifications = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_LOGS_TAB);
    var showReports = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_REPORTS_TAB);
    var showApprovalsExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_APPROVALS_TAB);
    var _f = React.useState(0), loadedChildren = _f[0], setLoadedChildren = _f[1];
    var _g = React.useState(0), value = _g[0], setValue = _g[1];
    var handleChange = function (event, newValue) {
        setValue(newValue);
    };
    useEffect(function () {
        if (record) {
            var locArr = location.pathname.split("/").slice(0, 3).join("/");
            navigate(locArr);
        }
    }, [location.pathname, navigate, record]);
    // Code to refresh the dashboard when the user navigates to it
    var handleChildMount = function () {
        setLoadedChildren(function (prev) { return (prev < 2 ? prev + 1 : prev); });
    };
    useEffect(function () {
        var _a;
        if (loadedChildren === 1 || loadedChildren === 2) {
            (_a = refreshRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    }, [loadedChildren]);
    if (!record) {
        return (_jsx(Box, { children: _jsx(CircularProgress, {}) }));
    }
    return (_jsxs(Box, __assign({ sx: { maxWidth: "calc(100vw - ".concat(open ? "352px" : "96px", ")"), bgcolor: "background.paper" }, className: "events-box" }, { children: [_jsx(ElectionHeader, { title: aliasRenderer(record), subtitle: "electionEventScreen.common.subtitle" }), _jsx(Box, __assign({ sx: {
                    bgcolor: "background.paper",
                } }, { children: _jsx(Tabs, { elements: __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], (showDashboard
                        ? [
                            {
                                label: t("electionEventScreen.tabs.dashboard"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Dashboard..." }) }, { children: _jsx(Box, __assign({ sx: { overflowX: "auto" } }, { children: _jsx(DashboardElectionEvent, { refreshRef: refreshRef, onMount: handleChildMount }) })) }))); },
                            },
                        ]
                        : []), true), (showMonitoringDashboard
                        ? [
                            {
                                label: t("electionEventScreen.tabs.monitoring"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Dashboard..." }) }, { children: _jsx(OVOFDashboardElectionEvent, { refreshRef: refreshRef, onMount: handleChildMount }) }))); },
                            },
                        ]
                        : []), true), (showData
                        ? [
                            {
                                label: t("electionEventScreen.tabs.data"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Data..." }) }, { children: _jsx(EditElectionEventData, {}) }))); },
                            },
                        ]
                        : []), true), (showTextData
                        ? [
                            {
                                label: t("electionEventScreen.tabs.localization"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Text Data..." }) }, { children: _jsx(EditElectionEventTextData, {}) }))); },
                            },
                        ]
                        : []), true), (showVoters
                        ? [
                            {
                                label: t("electionEventScreen.tabs.voters"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Voters..." }) }, { children: _jsx(EditElectionEventUsers, { electionEventId: record === null || record === void 0 ? void 0 : record.id }) }))); },
                            },
                        ]
                        : []), true), (showAreas
                        ? [
                            {
                                label: t("electionEventScreen.tabs.areas"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Areas..." }) }, { children: _jsx(EditElectionEventAreas, {}) }))); },
                            },
                        ]
                        : []), true), (showKeys
                        ? [
                            {
                                label: t("electionEventScreen.tabs.keys"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Keys..." }) }, { children: _jsx(EditElectionEventKeys, { isShowCeremony: showKeysList, isShowTrusteeCeremony: showKeysList }) }))); },
                                action: function () {
                                    setShowKeysList(uuidv4());
                                },
                            },
                        ]
                        : []), true), (showTally
                        ? [
                            {
                                label: t("electionEventScreen.tabs.tally"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Tally..." }) }, { children: _jsx(EditElectionEventTally, {}) }))); },
                                action: function () { return setTallyId(null); },
                            },
                        ]
                        : []), true), (showPublish
                        ? [
                            {
                                label: t("electionEventScreen.tabs.publish"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Publish..." }) }, { children: _jsx(Publish, { electionEventId: record === null || record === void 0 ? void 0 : record.id, type: EPublishType.Event, showList: showPublishList }) }))); },
                                action: function (index) {
                                    localStorage.setItem("electionEventPublishTabIndex", index.toString());
                                    setShowPublishList(uuidv4());
                                },
                            },
                        ]
                        : []), true), (showTasksExecution
                        ? [
                            {
                                label: t("electionEventScreen.tabs.tasks"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Tasks..." }) }, { children: _jsx(EditElectionEventTasks, { showList: showTaskList }) }))); },
                                action: function () {
                                    setShowTaskList(uuidv4());
                                },
                            },
                        ]
                        : []), true), (showLogs
                        ? [
                            {
                                label: t("electionEventScreen.tabs.logs"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Logs..." }) }, { children: _jsx(ElectoralLog, {}) }))); },
                            },
                        ]
                        : []), true), (showEvents
                        ? [
                            {
                                label: t("electionEventScreen.tabs.events"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Events..." }) }, { children: _jsx(EditElectionEventEvents, { electionEventId: record === null || record === void 0 ? void 0 : record.id }) }))); },
                            },
                        ]
                        : []), true), (showReports
                        ? [
                            {
                                label: t("electionEventScreen.tabs.reports"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Reports..." }) }, { children: _jsx(EditElectionEventReports, { electionEventId: record === null || record === void 0 ? void 0 : record.id }) }))); },
                            },
                        ]
                        : []), true), (showApprovalsExecution
                        ? [
                            {
                                label: t("electionEventScreen.tabs.approvals"),
                                component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Approvals..." }) }, { children: _jsx(EditElectionEventApprovals, { electionEventId: record === null || record === void 0 ? void 0 : record.id, showList: showApprovalList }) }))); },
                                action: function () {
                                    setShowApprovalList(uuidv4());
                                    localStorage.setItem("approvals_status_filter", "pending");
                                },
                            },
                        ]
                        : []), true) }) }))] })));
};
